import './Pagination.css';
import classNames from 'classnames';
import type { FC } from 'react';

export type PaginationNumbers = {
  currentPage: number;
  numberOfPages: number;
};

type Props = PaginationNumbers & {
  className?: string;
  displayedPages?: number;
  onPageChange: (page: number) => void;
};

const DEFAULT_DISPLAYED_PAGES = 5;

export const buildPaginationNumbers = (
  numberOfItems: number,
  page: number,
  pageSize: number,
): PaginationNumbers => {
  const numberOfPages = Math.ceil(numberOfItems / pageSize);
  const currentPage = page <= numberOfPages ? page : 0;
  return {
    numberOfPages,
    currentPage,
  };
};

export const Pagination: FC<Props> = (props) => {
  const displayedPages = props.displayedPages ?? DEFAULT_DISPLAYED_PAGES;
  const renderPages = () => {
    const firstPage = Math.max(
      0,
      Math.min(
        props.currentPage - Math.floor(displayedPages / 2),
        props.numberOfPages - displayedPages,
      ),
    );
    const result = [];
    for (
      let i = firstPage;
      i < Math.min(firstPage + displayedPages, props.numberOfPages);
      i += 1
    ) {
      result.push(
        <span
          key={i}
          onClick={() => {
            if (i !== props.currentPage) {
              props.onPageChange(i);
            }
          }}
          className={classNames(
            'pagination-item',
            i === props.currentPage && 'pagination-item--active',
          )}
        >
          {i + 1}
        </span>,
      );
    }
    return result;
  };

  return (
    <div className={classNames('flex flex-row items-center', props.className)}>
      <span
        className={classNames(
          'pagination-item pagination-item--arrow',
          props.currentPage === 0 && 'pagination-item--disabled',
        )}
        onClick={
          props.currentPage > 0
            ? () => {
                props.onPageChange(0);
              }
            : undefined
        }
      >
        <i className='fa fa-angle-double-left' />
      </span>
      <span
        className={classNames(
          'pagination-item pagination-item--arrow',
          props.currentPage === 0 && 'pagination-item--disabled',
        )}
        onClick={
          props.currentPage > 0
            ? () => {
                props.onPageChange(props.currentPage - 1);
              }
            : undefined
        }
      >
        <i className='fa fa-angle-left' />
      </span>
      {renderPages()}
      <span
        className={classNames(
          'pagination-item pagination-item--arrow',
          props.currentPage === props.numberOfPages - 1 &&
            'pagination-item--disabled',
        )}
        onClick={
          props.currentPage < props.numberOfPages - 1
            ? () => {
                props.onPageChange(props.currentPage + 1);
              }
            : undefined
        }
      >
        <i className='fa fa-angle-right' />
      </span>
      <span
        className={classNames(
          'pagination-item pagination-item--arrow',
          props.currentPage === props.numberOfPages - 1 &&
            'pagination-item--disabled',
        )}
        onClick={
          props.currentPage < props.numberOfPages - 1
            ? () => {
                props.onPageChange(props.numberOfPages - 1);
              }
            : undefined
        }
      >
        <i className='fa fa-angle-double-right' />
      </span>
    </div>
  );
};
